import { Circle, Polyline, Polygon } from 'react-leaflet';
import type { ReactNode } from 'react';
import type { Geometry } from '../../types/domain';

// Renders a Geometry (POINT circle / LINE polyline / BOX polygon) on the map.
export default function GeometryShape({
  geometry,
  color,
  fillColor,
  dashed = false,
  weight = 2,
  fillOpacity = 0.15,
  opacity = 1,
  children,
}: {
  geometry: Geometry;
  color: string;
  fillColor?: string; // fill colour override; falls back to `color` when unset (leaflet default)
  dashed?: boolean;
  weight?: number; // stroke thickness (default 2)
  fillOpacity?: number;
  opacity?: number; // stroke opacity (dimmed for scheduled/planned shapes)
  children?: ReactNode;
}) {
  const pathOptions = {
    color,
    fillColor,
    weight,
    fillOpacity,
    opacity,
    dashArray: dashed ? '6 6' : undefined,
  };

  if (geometry.shape === 'POINT') {
    if (geometry.centerLat == null || geometry.centerLon == null) return null;
    return (
      <Circle center={[geometry.centerLat, geometry.centerLon]} radius={geometry.radiusMeters ?? 50} pathOptions={pathOptions}>
        {children}
      </Circle>
    );
  }

  const pts = (geometry.coords ?? []).map((c) => [c.lat, c.lon] as [number, number]);
  if (geometry.shape === 'LINE' && pts.length >= 2) {
    return (
      <Polyline positions={pts} pathOptions={pathOptions}>
        {children}
      </Polyline>
    );
  }
  if (geometry.shape === 'BOX' && pts.length >= 3) {
    return (
      <Polygon positions={pts} pathOptions={pathOptions}>
        {children}
      </Polygon>
    );
  }
  return null;
}
